/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'

// Components
import {
    AccordionItem,
    AccordionButton,
    AccordionIcon,
    AccordionPanel,
    Box,
    HStack,
    Text,
    Flex,
    Image,
    Link as ChakraLink,
    Link
} from '@chakra-ui/react'

// Hooks
import MapPage from './google-maps'

const StoresList = ({storesInfo}) => {
    const intl = useIntl()

    const [activeStore, setActiveStore] = useState(null)

    const mappedStores = useMemo(() => {
        return (
            storesInfo?.map((store) => ({
                id: store.id,
                name: store.name,
                position: {
                    lat: store.latitude,
                    lng: store.longitude
                },
                infoWindowHtml: store.infoWindowHtml
            })) ?? []
        )
    }, [storesInfo])

    return (
        <Flex
            flexWrap={{base: 'wrap', lg: 'nowrap'}}
            flexDirection={{base: 'column', lg: 'row-reverse'}}
        >
            {!!storesInfo && (
                <Box width={{base: '100%', lg: '50%'}} minWidth={{base: '100%', lg: '50%'}}>
                    <MapPage locations={mappedStores} activeMarker={activeStore} />
                </Box>
            )}
            <Box
                width={{base: '100%', lg: '50%'}}
                minWidth={{base: '100%', lg: '50%'}}
                overflow="auto"
                maxHeight="500px"
            >
                {storesInfo?.map((store, index) => {
                    return (
                        <AccordionItem
                            key={index}
                            borderTop="1px solid"
                            borderColor={index > 0 ? 'inherit' : 'transparent'}
                            data-testid="store-element"
                        >
                            <HStack
                                align="flex-start"
                                mt={4}
                                mb={4}
                                maxW="calc(100% - 20px)"
                                onMouseEnter={() => {
                                    setActiveStore({
                                        position: {
                                            lat: Number(store.latitude),
                                            lng: Number(store.longitude)
                                        },
                                        infoWindowHtml: store.infoWindowHtml,
                                        id: store.id
                                    })
                                }}
                                onMouseLeave={() => {
                                    setActiveStore(null)
                                }}
                            >
                                <Text textStyle="Regular XLarge">{index + 1}</Text>
                                <Flex
                                    id={`store-info-${store.id}`}
                                    width="100%"
                                    flexDirection="column"
                                    gap={2}
                                    position="relative"
                                >
                                    <Flex
                                        flexDirection={{base: 'column', md: 'row'}}
                                        justifyContent="space-between"
                                    >
                                        {store.name && (
                                            <Text textStyle="Regular XLarge" isTruncated>
                                                {store.name}
                                            </Text>
                                        )}
                                        {store['c_store-logo'] && (
                                            <Image
                                                src={store['c_store-logo']}
                                                height="50px"
                                                paddingTop={{base: 2, md: 0}}
                                                width="auto"
                                                objectFit="contain"
                                                alignSelf="start"
                                            />
                                        )}
                                    </Flex>
                                    <Box textStyle="Regular Medium" color="stokkeGray.dark">
                                        {store.address1}
                                    </Box>
                                    <Box textStyle="Regular Medium" color="stokkeGray.dark">
                                        <span>{store.city}</span>
                                        <span>,&nbsp;</span>
                                        <span>{store.stateCode ? store.stateCode : ''}</span>{' '}
                                        <span>{store.postalCode}</span>
                                    </Box>
                                    {store.phone && (
                                        <Box textStyle="Regular Medium" color="stokkeGray.dark">
                                            {intl.formatMessage({
                                                id: 'store_locator.description.phone',
                                                defaultMessage: 'Phone:'
                                            })}{' '}
                                            <Link
                                                href={`tel:${store.phone}`}
                                                color="stokkeGray.dark"
                                            >
                                                {store.phone}
                                            </Link>
                                        </Box>
                                    )}
                                    {store.c_website && (
                                        <Text
                                            color="stokkeGray.dark"
                                            isTruncated
                                            maxW="100%"
                                            whiteSpace="nowrap"
                                            overflow="hidden"
                                            textOverflow="ellipsis"
                                        >
                                            {intl.formatMessage({
                                                id: 'store_locator.website',
                                                defaultMessage: 'Website:'
                                            })}
                                            <span> </span>
                                            <ChakraLink
                                                color="stokkeGray.dark"
                                                href={store.c_website}
                                                size="md"
                                                isExternal
                                                variant="underlineLink"
                                            >
                                                {store.c_website}
                                            </ChakraLink>
                                        </Text>
                                    )}
                                    <Flex gap={2} flexWrap="wrap">
                                        {store.distance !== undefined && (
                                            <Flex layerStyle="chip">
                                                {store.distance + ' ' + store.distanceUnit}
                                            </Flex>
                                        )}
                                        {store['c_full-partner'] && (
                                            <Flex layerStyle="chip">
                                                <FormattedMessage
                                                    defaultMessage="Full assortment"
                                                    id="store_locator.full_partner"
                                                />
                                            </Flex>
                                        )}
                                        {store['c_destination-partner'] && (
                                            <Flex layerStyle="chip">
                                                <FormattedMessage
                                                    defaultMessage="Destination store"
                                                    id="store_locator.destination_store"
                                                />
                                            </Flex>
                                        )}
                                    </Flex>
                                    {(store.storeHours || store.c_sells) && (
                                        <>
                                            {' '}
                                            <AccordionButton
                                                sx={{marginTop: '10px', paddingBottom: '0px'}}
                                            >
                                                <Text textStyle="Regular XLarge">
                                                    {intl.formatMessage({
                                                        id: 'store_locator.action.viewMore',
                                                        defaultMessage: 'View More'
                                                    })}
                                                </Text>
                                                <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel mb={6} mt={4}>
                                                {store?.storeHours && (
                                                    <Box
                                                        color="stokkeGray.dark"
                                                        dangerouslySetInnerHTML={{
                                                            __html: store.storeHours
                                                        }}
                                                    />
                                                )}
                                                {store.c_sells && (
                                                    <>
                                                        <Text
                                                            textStyle="Regular Medium"
                                                            color="stokkeGray.dark"
                                                            paddingY={2}
                                                        >
                                                            <FormattedMessage
                                                                defaultMessage="products:"
                                                                id="store_locator.products"
                                                            />
                                                        </Text>
                                                        <Flex gap={2} flexWrap="wrap">
                                                            {store.c_sells?.map((product) => (
                                                                <Flex
                                                                    key={`${store.id}-${product}`}
                                                                    layerStyle="chip"
                                                                >
                                                                    {product}
                                                                </Flex>
                                                            ))}
                                                        </Flex>
                                                    </>
                                                )}
                                            </AccordionPanel>{' '}
                                        </>
                                    )}
                                </Flex>
                            </HStack>
                        </AccordionItem>
                    )
                })}
            </Box>
        </Flex>
    )
}

StoresList.propTypes = {
    storesInfo: PropTypes.array
}

export default StoresList
